import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Lock as LockIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  PhotoCamera as PhotoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useToast } from '../../contexts/ToastContext';
import betTrackerService from '../../services/betTrackerService';
import BackButton from '../BackButton';
import { fetchScoresForSport, fetchOdds } from '../../services/oddsApi';
import { findRepeatedTeams } from '../../services/betTracker/exposure';
import {
  computeHedge,
  hedgeTargetLeg,
  remainingLegs,
  findHedgePrice
} from '../../services/betTracker/hedge';
import { summarizeBets, breakdownBySport, breakdownByBetType, breakdownByBook } from '../../services/betTracker/engine/stats';
import { americanToDecimal } from '../../services/betTracker/engine/model';
import { parseBetText } from '../../services/betTracker/ingest/textParse';
import { classifyShareUrl } from '../../services/betTracker/ingest/linkParse';
import { extractFieldsFromImage, imageDraftFromExtraction } from '../../services/betTracker/ingest/imageIntake';
import { betsFromCsv } from '../../services/betTracker/sync/csvImport';
import {
  toModelBet,
  draftToBetRecord,
  modelBetToRecord,
  buildRecord,
  manualOverrideUpdates,
  sportLabelFor,
  sportKeyForSport,
  selectionLabel,
  legEventLabel
} from '../../services/betTracker/records';
import { autoGradeRecords } from '../../services/betTracker/autoGrade';

/**
 * Bet Tracker — production port of the parked bet-hub prototype
 * ("the way we had it before"): stats dashboard (record, win rate,
 * ROI, net profit, streaks, biggest/closest results, breakdowns),
 * add-a-bet (manual form, paste text or a sportsbook share link,
 * screenshot, CSV import) through an editable confirm card that flags
 * missing fields and never guesses, and auto-grading of pending bets
 * against final scores from the site's scores pipeline.
 *
 * Premium-only (Yianni, 2026-10-31), on top of the route's login
 * gate: non-premium users get the same lock/upsell gate view as the
 * Parlay Builder (useSubscription + /subscription), following the
 * site's FeaturedPicks premium pattern.
 *
 * Bets persist per user at users/{uid}/bets/{betId} (see
 * betTrackerService). Auto-grading never settles a bet on missing
 * data — unmatchable bets stay pending for the manual override,
 * and settled rows are labeled "Auto-graded" vs "Marked by you"
 * (settledBy) so the two are never confused.
 *
 * Winning Features addendum (2026-11-02): an Exposure Guard banner
 * (services/betTracker/exposure) flags teams sitting on 2+ pending
 * tickets, and each pending ticket gets a Hedge Calculator
 * (services/betTracker/hedge, Premium) that prices the equal-profit
 * hedge on its last open leg from the live feed, with a manual-price
 * fallback.
 */

const DISCLAIMER =
  '21+. For information and entertainment only. Not a sportsbook. No pick is guaranteed. Bet only where legal. Gambling problem? Call 1-800-GAMBLER (NY: 877-8-HOPENY).';

const SPORT_OPTIONS = [
  { key: 'americanfootball_nfl', label: 'NFL' },
  { key: 'americanfootball_ncaaf', label: 'NCAAF' },
  { key: 'basketball_nba', label: 'NBA' },
  { key: 'basketball_ncaab', label: 'NCAAB' },
  { key: 'basketball_wnba', label: 'WNBA' },
  { key: 'baseball_mlb', label: 'MLB' },
  { key: 'icehockey_nhl', label: 'NHL' },
  { key: 'soccer_epl', label: 'Soccer (EPL)' },
  { key: 'mma_mixed_martial_arts', label: 'MMA / UFC' }
];

const MARKET_OPTIONS = [
  { key: 'h2h', label: 'Moneyline' },
  { key: 'spreads', label: 'Spread' },
  { key: 'totals', label: 'Total (Over/Under)' }
];

const BET_TYPE_OPTIONS = [
  { key: 'moneyline', label: 'Moneyline' },
  { key: 'spread', label: 'Spread' },
  { key: 'total', label: 'Total' },
  { key: 'prop', label: 'Prop' }
];

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_IMAGE_WIDTH = 1024;

const formatAmerican = (american) =>
  typeof american === 'number' ? (american > 0 ? `+${american}` : `${american}`) : '—';

const formatMoney = (value) =>
  typeof value === 'number'
    ? `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—';

const formatSignedMoney = (value) => {
  if (typeof value !== 'number') return '—';
  const text = formatMoney(Math.abs(value));
  return value > 0 ? `+${text}` : value < 0 ? `-${text}` : text;
};

const formatPct = (fraction) =>
  fraction === null || fraction === undefined ? '—' : `${(fraction * 100).toFixed(1)}%`;

const formatStartEt = (iso) => {
  if (!iso) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    }).formatToParts(new Date(iso));
    const get = (type) => (parts.find((p) => p.type === type) || {}).value || '';
    return `${get('month')} ${get('day')} · ${get('hour')}:${get('minute')} ${get('dayPeriod')} ET`;
  } catch (e) {
    return '';
  }
};

const STATUS_CHIP = {
  pending: { label: 'Pending', color: 'default' },
  won: { label: 'Won', color: 'success' },
  lost: { label: 'Lost', color: 'error' },
  push: { label: 'Push', color: 'warning' }
};

const SOURCE_LABEL = {
  'parlay-builder': 'Parlay Builder',
  manual: 'Manual',
  paste: 'Pasted',
  csv: 'CSV import',
  screenshot: 'Screenshot'
};

/** Read a file as a data URL directly, with no browser image decoding. */
function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('image read failed'));
    reader.readAsDataURL(file);
  });
}

/**
 * Downscale an image file to <=1024px wide JPEG (data URL) for upload.
 * If the browser cannot decode the file into an <img> (some phones hand
 * over photos their own browser won't decode), fall back to uploading
 * the original file as-is — the reader function accepts full-size
 * PNG/JPEG/WebP up to 8 MB and the screenshot is read server-side, so
 * the downscale is a cost optimization, not a gate.
 */
function downscaleImageFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const scale = Math.min(1, MAX_IMAGE_WIDTH / img.naturalWidth);
        const width = Math.max(1, Math.round(img.naturalWidth * scale));
        const height = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      } catch (err) {
        reject(err);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('image load failed'));
    };
    img.src = url;
  }).catch(() => fileToDataUrl(file));
}

const emptyLegForm = () => ({
  sport: 'americanfootball_nfl',
  market: 'h2h',
  name: '',
  awayTeam: '',
  homeTeam: '',
  point: '',
  american: '',
  date: ''
});

const BetTrackerScreen = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isPremium, loading: subscriptionLoading } = useSubscription();
  const { showToast } = useToast();

  const [bets, setBets] = useState([]);
  const [betsLoading, setBetsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [gradingBusy, setGradingBusy] = useState(false);
  const [gradeNote, setGradeNote] = useState(null);
  const [expandedIds, setExpandedIds] = useState({});

  // Hedge Calculator (Winning Features addendum, Premium): one open
  // panel at a time. phase: idle -> loading -> done | error; a manual
  // price entry is always available as the fallback/override.
  const [hedgePanel, setHedgePanel] = useState(null);

  const [addTab, setAddTab] = useState(0);
  const [legForms, setLegForms] = useState([emptyLegForm()]);
  const [manualStake, setManualStake] = useState('');
  const [manualBook, setManualBook] = useState('');
  const [manualError, setManualError] = useState(null);

  const [pasteText, setPasteText] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteError, setPasteError] = useState(null);
  const [draftBundle, setDraftBundle] = useState(null);
  const [confirmError, setConfirmError] = useState(null);

  const [shotBusy, setShotBusy] = useState(false);
  const [shotError, setShotError] = useState(null);
  const [shotNote, setShotNote] = useState(null);

  const [csvBusy, setCsvBusy] = useState(false);
  const [csvReport, setCsvReport] = useState(null);

  const betsRef = useRef([]);
  const gradedOnceRef = useRef(false);
  betsRef.current = bets;

  const uid = currentUser ? currentUser.uid : null;

  useEffect(() => {
    if (!uid || !isPremium) {
      setBetsLoading(false);
      return undefined;
    }
    setBetsLoading(true);
    const unsubscribe = betTrackerService.subscribeBets(
      uid,
      (records) => {
        setBets(records);
        setBetsLoading(false);
      },
      () => {
        setLoadError('Could not load your bets. Please refresh.');
        setBetsLoading(false);
      }
    );
    return unsubscribe;
  }, [uid, isPremium]);

  const runGrading = async (manual) => {
    if (!uid || gradingBusy) return;
    setGradingBusy(true);
    try {
      const updates = await autoGradeRecords(betsRef.current, fetchScoresForSport);
      for (const entry of updates) {
        await betTrackerService.updateBet(uid, entry.id, entry.updates);
      }
      if (updates.length > 0) {
        showToast(
          `Settled ${updates.length} bet${updates.length === 1 ? '' : 's'} from final scores`,
          'success'
        );
      } else if (manual) {
        setGradeNote('No pending bets could be settled yet — unmatched bets stay pending for manual marking.');
      }
    } catch (err) {
      if (manual) setGradeNote('Could not check results right now — your bets are unchanged.');
    } finally {
      setGradingBusy(false);
    }
  };

  const runGradingRef = useRef(runGrading);
  runGradingRef.current = runGrading;

  // Auto-grade once when the first batch of bets arrives.
  useEffect(() => {
    if (!betsLoading && bets.length > 0 && !gradedOnceRef.current) {
      gradedOnceRef.current = true;
      runGradingRef.current(false);
    }
  }, [betsLoading, bets.length]);

  const modelBets = useMemo(() => bets.map(toModelBet), [bets]);
  const summary = useMemo(() => (bets.length ? summarizeBets(modelBets) : null), [bets, modelBets]);
  const sportBreakdown = useMemo(() => (bets.length ? breakdownBySport(modelBets) : []), [bets, modelBets]);
  const typeBreakdown = useMemo(() => (bets.length ? breakdownByBetType(modelBets) : []), [bets, modelBets]);
  const hasBookData = useMemo(() => bets.some((b) => b.book), [bets]);
  const bookBreakdown = useMemo(
    () => (bets.length && hasBookData ? breakdownByBook(modelBets) : []),
    [bets, modelBets, hasBookData]
  );

  const sortedBets = useMemo(() => {
    const pending = bets.filter((b) => b.status === 'pending');
    const settled = bets.filter((b) => b.status !== 'pending');
    const byCreatedDesc = (a, b) => String(b.createdAt).localeCompare(String(a.createdAt));
    pending.sort(byCreatedDesc);
    settled.sort((a, b) =>
      String(b.settledAt || b.createdAt).localeCompare(String(a.settledAt || a.createdAt)));
    return [...pending, ...settled];
  }, [bets]);

  // Exposure Guard (Winning Features addendum): teams sitting on 2+
  // pending tickets.
  const exposureRepeats = useMemo(() => findRepeatedTeams(bets), [bets]);

  // ---------------------------------------------------------------
  // Hedge Calculator
  // ---------------------------------------------------------------
  const openHedgePanel = (bet) => {
    setHedgePanel((prev) =>
      prev && prev.betId === bet.id
        ? null
        : { betId: bet.id, phase: 'idle', priceInfo: null, manualOdds: '' }
    );
  };

  const findLiveHedgePrice = async (bet) => {
    const target = hedgeTargetLeg(bet);
    if (!target) return;
    setHedgePanel((prev) => (prev ? { ...prev, phase: 'loading' } : prev));
    try {
      const sportKey = sportKeyForSport(target.sport);
      if (!sportKey) throw new Error('Unknown sport for this leg');
      const events = await fetchOdds(sportKey);
      const found = findHedgePrice(events, target);
      setHedgePanel((prev) =>
        prev ? { ...prev, phase: 'done', priceInfo: found } : prev
      );
    } catch (err) {
      setHedgePanel((prev) =>
        prev ? { ...prev, phase: 'error', priceInfo: null } : prev
      );
    }
  };

  // ---------------------------------------------------------------
  // Manual add
  // ---------------------------------------------------------------
  const updateLegForm = (index, field, value) => {
    setLegForms((prev) => prev.map((leg, i) => (i === index ? { ...leg, [field]: value } : leg)));
  };

  const handleManualSave = async () => {
    setManualError(null);
    const stake = parseFloat(manualStake);
    if (!stake || stake <= 0) {
      setManualError('Enter a stake greater than $0.');
      return;
    }
    const legs = [];
    for (const [i, form] of legForms.entries()) {
      const american = parseInt(form.american, 10);
      if (!form.name.trim() || !form.awayTeam.trim() || !form.homeTeam.trim()) {
        setManualError(`Leg ${i + 1}: selection and both teams are required.`);
        return;
      }
      if (!Number.isInteger(american) || Math.abs(american) < 100) {
        setManualError(`Leg ${i + 1}: enter valid American odds (e.g. -110 or +150).`);
        return;
      }
      let point = null;
      if (form.market !== 'h2h') {
        point = parseFloat(form.point);
        if (!Number.isFinite(point)) {
          setManualError(`Leg ${i + 1}: a spread/total needs its line (e.g. -3.5 or 47.5).`);
          return;
        }
      }
      const leg = {
        ...(form.sport ? { sport: form.sport } : { sport: '' }),
        market: form.market,
        name: form.name.trim(),
        point,
        american,
        decimal: americanToDecimal(american),
        homeTeam: form.homeTeam.trim(),
        awayTeam: form.awayTeam.trim(),
        commenceTime: form.date ? new Date(form.date).toISOString() : new Date().toISOString()
      };
      leg.selection = selectionLabel(leg);
      legs.push(leg);
    }
    try {
      const record = buildRecord({
        legs,
        stake,
        book: manualBook.trim() || null,
        source: 'manual'
      });
      await betTrackerService.addBet(uid, record);
      showToast('Bet added to your tracker', 'success');
      setLegForms([emptyLegForm()]);
      setManualStake('');
      setManualBook('');
    } catch (err) {
      setManualError('Could not save that bet — please try again.');
    }
  };

  // ---------------------------------------------------------------
  // Paste (text or share link) + screenshot -> confirm card
  // ---------------------------------------------------------------
  const handleParsePaste = async () => {
    const text = pasteText.trim();
    if (!text || pasteBusy) return;
    setPasteError(null);
    setConfirmError(null);
    if (/^https?:\/\/\S+$/i.test(text)) {
      const classified = classifyShareUrl(text);
      if (!classified.recognized) {
        setPasteError('We don\'t recognize that sportsbook\'s links yet — paste the slip text instead and we\'ll parse that.');
        return;
      }
      setPasteBusy(true);
      try {
        const idToken = await currentUser.getIdToken();
        const response = await fetch('/.netlify/functions/betslip-fetch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify({ url: text })
        });
        const payload = await response.json().catch(() => null);
        if (payload && payload.ok && payload.draft) {
          setDraftBundle({
            draft: payload.draft,
            missing: payload.missing || [],
            warnings: payload.warnings || [],
            source: 'paste'
          });
        } else {
          setPasteError((payload && payload.error) || 'Could not read that link — paste the slip text instead.');
        }
      } catch (err) {
        setPasteError('Could not read that link — paste the slip text instead.');
      } finally {
        setPasteBusy(false);
      }
      return;
    }
    const parsed = parseBetText(text);
    if (parsed.draft) {
      setDraftBundle({
        draft: parsed.draft,
        missing: parsed.missing || [],
        warnings: parsed.warnings || [],
        source: 'paste'
      });
    } else {
      setPasteError('We couldn\'t parse that text — check it, or enter the bet manually.');
    }
  };

  const handleScreenshotFile = async (file) => {
    if (!file || shotBusy) return;
    setShotError(null);
    setShotNote(null);
    setConfirmError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setShotError('That image is over 8 MB — please upload a smaller screenshot.');
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      setShotError('Please upload a PNG or JPG screenshot.');
      return;
    }
    setShotBusy(true);
    try {
      const dataUrl = await downscaleImageFile(file);
      const idToken = await currentUser.getIdToken();
      const result = await extractFieldsFromImage({ dataUrl }, { idToken });
      if (!result.ok) {
        if (result.enabled === false) setShotNote(result.error);
        else setShotError(result.error);
      } else {
        const bundle = imageDraftFromExtraction(result.extraction);
        if (bundle.draft) {
          setDraftBundle({ ...bundle, source: 'screenshot' });
        } else {
          setShotError('We couldn\'t read a bet from that screenshot.');
        }
      }
    } catch (err) {
      setShotError('Something went wrong reading that screenshot — paste the slip text instead.');
    } finally {
      setShotBusy(false);
    }
  };

  const updateDraft = (updater) => {
    setDraftBundle((prev) => (prev ? { ...prev, draft: updater(prev.draft) } : prev));
    setConfirmError(null);
  };

  const handleConfirmDraft = async () => {
    if (!draftBundle) return;
    const draft = draftBundle.draft;
    const stillMissing = [];
    if (typeof draft.oddsAmerican !== 'number') stillMissing.push('odds');
    if (!draft.stake || draft.stake <= 0) stillMissing.push('stake');
    if (draft.betType !== 'parlay' && !draft.selection) stillMissing.push('selection');
    if (stillMissing.length > 0) {
      setConfirmError(`Still needed before saving: ${stillMissing.join(', ')}. We never guess these — fill them in from your slip.`);
      return;
    }
    try {
      const record = draftToBetRecord(draft, draftBundle.source);
      await betTrackerService.addBet(uid, record);
      showToast('Bet added to your tracker', 'success');
      setDraftBundle(null);
      setPasteText('');
    } catch (err) {
      setConfirmError('Could not save that bet — please try again.');
    }
  };

  // ---------------------------------------------------------------
  // CSV import
  // ---------------------------------------------------------------
  const handleCsvFile = async (file) => {
    if (!file || csvBusy) return;
    setCsvBusy(true);
    setCsvReport(null);
    try {
      const text = await file.text();
      const { bets: parsed, errors } = betsFromCsv(text);
      let imported = 0;
      let skippedCashedOut = 0;
      for (const bet of parsed) {
        const record = modelBetToRecord(bet, 'csv');
        if (!record) {
          skippedCashedOut += 1;
          continue;
        }
        await betTrackerService.addBet(uid, record);
        imported += 1;
      }
      setCsvReport({
        imported,
        skippedCashedOut,
        errors: errors || []
      });
      if (imported > 0) showToast(`Imported ${imported} bet${imported === 1 ? '' : 's'}`, 'success');
    } catch (err) {
      setCsvReport({ imported: 0, skippedCashedOut: 0, errors: [{ line: 0, message: 'Could not read that file.' }] });
    } finally {
      setCsvBusy(false);
    }
  };

  // ---------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------
  const handleOverride = async (bet, outcome) => {
    try {
      await betTrackerService.updateBet(uid, bet.id, manualOverrideUpdates(bet, outcome));
      showToast(`Marked as ${outcome}`, 'success');
    } catch (err) {
      showToast('Could not update that bet.', 'error');
    }
  };

  const handleDelete = async (bet) => {
    if (!window.confirm('Delete this bet from your tracker?')) return;
    try {
      await betTrackerService.deleteBet(uid, bet.id);
      showToast('Bet deleted', 'info');
    } catch (err) {
      showToast('Could not delete that bet.', 'error');
    }
  };

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // ---------------------------------------------------------------
  // Render: gate + loading states
  // ---------------------------------------------------------------
  if (subscriptionLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isPremium) {
    return (
      <Box>
        <BackButton />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
          Bet Tracker
        </Typography>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 5 }}>
            <LockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              Premium Feature
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: 'auto' }}>
              The Bet Tracker is a Premium feature — track every bet with record, ROI and streak
              stats, add bets by paste, share link or screenshot, and let final scores grade them
              for you.
            </Typography>
            <Button variant="contained" onClick={() => navigate('/subscription')}>
              Upgrade to Premium
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const streakText = summary && summary.currentStreak
    ? `${summary.currentStreak.type}${summary.currentStreak.length}`
    : '—';

  const renderBreakdown = (title, rows) => (
    <Box sx={{ flex: 1, minWidth: 240 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No bets yet.</Typography>
      ) : (
        <Stack spacing={0.75}>
          {rows.map((row) => (
            <Box key={row.key} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {row.label}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.wins}W-{row.losses}L{row.pushes ? `-${row.pushes}P` : ''}
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, color: row.netProfit > 0 ? 'success.main' : row.netProfit < 0 ? 'error.main' : 'inherit' }}
              >
                {formatSignedMoney(row.netProfit)}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderHedgePanel = (bet) => {
    if (!hedgePanel || hedgePanel.betId !== bet.id) return null;
    const target = hedgeTargetLeg(bet);
    const remaining = remainingLegs(bet);
    if (!target) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          No legs left to hedge on this ticket.
        </Typography>
      );
    }
    const manualAmerican = parseFloat(hedgePanel.manualOdds);
    const manualDecimal =
      Number.isFinite(manualAmerican) && manualAmerican !== 0
        ? americanToDecimal(manualAmerican)
        : null;
    const hedgeDecimal = manualDecimal || (hedgePanel.priceInfo && hedgePanel.priceInfo.decimal) || null;
    const figures = hedgeDecimal
      ? computeHedge({
          stake: bet.stake,
          combinedDecimal: bet.combinedDecimal,
          hedgeDecimal
        })
      : null;
    return (
      <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Hedge calculator
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Last leg: <strong>{selectionLabel(target)}</strong> · {legEventLabel(target)} ·{' '}
          {formatStartEt(target.commenceTime)}
          {remaining.length > 1 &&
            ` · ${remaining.length} legs still open — these figures assume every other open leg wins.`}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => findLiveHedgePrice(bet)}
            disabled={hedgePanel.phase === 'loading'}
            sx={{ fontWeight: 700 }}
          >
            {hedgePanel.phase === 'loading' ? 'Checking prices…' : 'Find live hedge price'}
          </Button>
          <TextField
            size="small"
            label="Hedge price (American)"
            placeholder="-110"
            value={hedgePanel.manualOdds}
            onChange={(e) =>
              setHedgePanel((prev) =>
                prev ? { ...prev, manualOdds: e.target.value.replace(/[^0-9.+-]/g, '') } : prev
              )
            }
            sx={{ width: 190 }}
          />
        </Stack>

        {hedgePanel.phase === 'done' && hedgePanel.priceInfo && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            Live price for the other side: <strong>{formatAmerican(hedgePanel.priceInfo.american)}</strong>{' '}
            at {hedgePanel.priceInfo.bookmakerTitle} (best available).
          </Typography>
        )}
        {hedgePanel.phase === 'done' && !hedgePanel.priceInfo && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No live price found for the other side right now — enter the current price above and
            the hedge is figured from that.
          </Typography>
        )}
        {hedgePanel.phase === 'error' && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Couldn&apos;t reach the odds feed — enter the current price above instead.
          </Typography>
        )}

        {figures && (
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Bet <strong>{formatMoney(figures.hedgeStake)}</strong> on the other side at{' '}
            {manualDecimal ? 'your price' : 'the live price'} to lock in{' '}
            <strong>{formatSignedMoney(figures.guaranteedProfit)}</strong> either way
            {figures.guaranteedProfit < 0 &&
              ' — a locked loss, but smaller than losing the whole stake if the last leg misses'}
            .
          </Typography>
        )}
        {hedgeDecimal && !figures && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            That price can&apos;t hedge this ticket — check the odds and try again.
          </Typography>
        )}
      </Box>
    );
  };

  const renderConfirmCard = () => {
    if (!draftBundle) return null;
    const draft = draftBundle.draft;
    const missing = draftBundle.missing || [];
    const isParlayDraft = draft.betType === 'parlay' && Array.isArray(draft.legs);
    const fieldError = (name) => missing.includes(name);
    return (
      <Card sx={{ mt: 2, borderColor: '#F97316', borderWidth: 1, borderStyle: 'solid' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Confirm this bet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We filled in what we could read. Anything flagged wasn&apos;t found — fill it in from
            your slip; we never guess odds, lines or stakes.
          </Typography>
          {(draftBundle.warnings || []).map((warning) => (
            <Alert key={warning} severity="info" sx={{ mb: 1 }}>
              {warning}
            </Alert>
          ))}
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Sportsbook"
                size="small"
                fullWidth
                value={draft.book ? draft.book.name : ''}
                error={fieldError('book')}
                helperText={fieldError('book') ? 'Not found — enter the book' : ' '}
                onChange={(e) => updateDraft((d) => ({
                  ...d,
                  book: { key: d.book ? d.book.key : 'unknown', name: e.target.value }
                }))}
              />
              <TextField
                label="Sport"
                size="small"
                fullWidth
                value={draft.sport || ''}
                onChange={(e) => updateDraft((d) => ({ ...d, sport: e.target.value, league: e.target.value }))}
              />
            </Stack>
            {!isParlayDraft && (
              <>
                <TextField
                  label="Matchup (Away at Home)"
                  size="small"
                  fullWidth
                  value={draft.event || ''}
                  onChange={(e) => updateDraft((d) => ({ ...d, event: e.target.value }))}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Selection"
                    size="small"
                    fullWidth
                    value={draft.selection || ''}
                    error={fieldError('selection')}
                    helperText={fieldError('selection') ? 'Not found — enter your pick' : ' '}
                    onChange={(e) => updateDraft((d) => ({ ...d, selection: e.target.value }))}
                  />
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Market</InputLabel>
                    <Select
                      label="Market"
                      value={draft.betType || 'moneyline'}
                      onChange={(e) => updateDraft((d) => ({ ...d, betType: e.target.value }))}
                    >
                      {BET_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.key} value={option.key}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {(draft.betType === 'spread' || draft.betType === 'total') && (
                    <TextField
                      label="Line"
                      size="small"
                      sx={{ width: 110 }}
                      value={draft.line ?? ''}
                      onChange={(e) => updateDraft((d) => ({
                        ...d,
                        line: e.target.value === '' ? null : parseFloat(e.target.value)
                      }))}
                    />
                  )}
                </Stack>
              </>
            )}
            {isParlayDraft && (
              <Stack spacing={1.5}>
                {draft.legs.map((leg, i) => (
                  <Stack key={i} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <TextField
                      label={`Leg ${i + 1} matchup`}
                      size="small"
                      fullWidth
                      value={leg.event || ''}
                      onChange={(e) => updateDraft((d) => ({
                        ...d,
                        legs: d.legs.map((l, j) => (j === i ? { ...l, event: e.target.value } : l))
                      }))}
                    />
                    <TextField
                      label="Selection"
                      size="small"
                      fullWidth
                      value={leg.selection || ''}
                      onChange={(e) => updateDraft((d) => ({
                        ...d,
                        legs: d.legs.map((l, j) => (j === i ? { ...l, selection: e.target.value } : l))
                      }))}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <InputLabel>Market</InputLabel>
                      <Select
                        label="Market"
                        value={leg.betType || 'moneyline'}
                        onChange={(e) => updateDraft((d) => ({
                          ...d,
                          legs: d.legs.map((l, j) => (j === i ? { ...l, betType: e.target.value } : l))
                        }))}
                      >
                        {BET_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option.key} value={option.key}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {(leg.betType === 'spread' || leg.betType === 'total') && (
                      <TextField
                        label="Line"
                        size="small"
                        sx={{ width: 90 }}
                        value={leg.line ?? ''}
                        onChange={(e) => updateDraft((d) => ({
                          ...d,
                          legs: d.legs.map((l, j) => (j === i
                            ? { ...l, line: e.target.value === '' ? null : parseFloat(e.target.value) }
                            : l))
                        }))}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Odds (American)"
                size="small"
                sx={{ width: 170 }}
                value={draft.oddsAmerican ?? ''}
                error={fieldError('odds')}
                helperText={fieldError('odds') ? 'Not found — enter the odds' : ' '}
                onChange={(e) => updateDraft((d) => ({
                  ...d,
                  oddsAmerican: e.target.value === '' ? null : parseInt(e.target.value, 10)
                }))}
              />
              <TextField
                label="Stake ($)"
                size="small"
                sx={{ width: 170 }}
                value={draft.stake || ''}
                error={fieldError('stake')}
                helperText={fieldError('stake') ? 'Not found — enter your stake' : ' '}
                onChange={(e) => updateDraft((d) => ({
                  ...d,
                  stake: e.target.value === '' ? 0 : parseFloat(e.target.value)
                }))}
              />
            </Stack>
            {confirmError && <Alert severity="error">{confirmError}</Alert>}
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={handleConfirmDraft} sx={{ fontWeight: 700 }}>
                Save to tracker
              </Button>
              <Button
                onClick={() => {
                  setDraftBundle(null);
                  setConfirmError(null);
                }}
              >
                Discard
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <BackButton />
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            Bet Tracker
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Every bet you track, graded against final scores — with the record and ROI to prove it.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={gradingBusy ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={() => runGrading(true)}
          disabled={gradingBusy || bets.length === 0}
        >
          Refresh results
        </Button>
      </Box>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {gradeNote && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setGradeNote(null)}>
          {gradeNote}
        </Alert>
      )}

      {/* Stats header */}
      {summary && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 2,
                textAlign: 'center',
                mb: 2
              }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">RECORD</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {summary.wins}W-{summary.losses}L{summary.pushes ? `-${summary.pushes}P` : ''}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">WIN RATE</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {formatPct(summary.winRate)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">ROI</Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: summary.roi > 0 ? 'success.main' : summary.roi < 0 ? 'error.main' : 'inherit' }}
                >
                  {formatPct(summary.roi)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">NET PROFIT</Typography>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 800, color: summary.netProfit > 0 ? 'success.main' : summary.netProfit < 0 ? 'error.main' : 'inherit' }}
                >
                  {formatSignedMoney(summary.netProfit)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">STREAK</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {streakText}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">PENDING EXPOSURE</Typography>
                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                  {formatMoney(summary.pendingExposure)}
                </Typography>
              </Box>
            </Box>
            {(summary.biggestWin || summary.closestWin || summary.biggestLoss || summary.closestLoss) && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 2 }}>
                {summary.biggestWin && <>Biggest win {formatSignedMoney(summary.biggestWin.profit)} </>}
                {summary.closestWin && <>· Closest win by {summary.closestWin.margin} </>}
                {summary.biggestLoss && <>· Biggest loss {formatSignedMoney(summary.biggestLoss.profit)} </>}
                {summary.closestLoss && <>· Closest loss by {Math.abs(summary.closestLoss.margin)}</>}
              </Typography>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              {renderBreakdown('By sport', sportBreakdown)}
              {renderBreakdown('By bet type', typeBreakdown)}
              {hasBookData && renderBreakdown('By sportsbook', bookBreakdown)}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Add a bet */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Add a bet
          </Typography>
          <Tabs value={addTab} onChange={(_, v) => setAddTab(v)} sx={{ mb: 2 }}>
            <Tab label="Manual" />
            <Tab label="Paste a bet" />
            <Tab label="Screenshot" />
            <Tab label="CSV import" />
          </Tabs>

          {addTab === 0 && (
            <Stack spacing={2}>
              {legForms.map((form, i) => (
                <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                      {legForms.length > 1 ? `Leg ${i + 1}` : 'Bet'}
                    </Typography>
                    {legForms.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={`Remove leg ${i + 1}`}
                        onClick={() => setLegForms((prev) => prev.filter((_, j) => j !== i))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  <Stack spacing={1.5}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Sport</InputLabel>
                        <Select
                          label="Sport"
                          value={form.sport}
                          onChange={(e) => updateLegForm(i, 'sport', e.target.value)}
                        >
                          {SPORT_OPTIONS.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 170 }}>
                        <InputLabel>Market</InputLabel>
                        <Select
                          label="Market"
                          value={form.market}
                          onChange={(e) => updateLegForm(i, 'market', e.target.value)}
                        >
                          {MARKET_OPTIONS.map((option) => (
                            <MenuItem key={option.key} value={option.key}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField
                        label={form.market === 'totals' ? 'Over / Under' : 'Team / selection'}
                        size="small"
                        fullWidth
                        value={form.name}
                        onChange={(e) => updateLegForm(i, 'name', e.target.value)}
                      />
                      {form.market !== 'h2h' && (
                        <TextField
                          label="Line"
                          size="small"
                          sx={{ width: 100 }}
                          value={form.point}
                          onChange={(e) => updateLegForm(i, 'point', e.target.value)}
                        />
                      )}
                      <TextField
                        label="Odds"
                        size="small"
                        sx={{ width: 110 }}
                        value={form.american}
                        onChange={(e) => updateLegForm(i, 'american', e.target.value)}
                      />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                      <TextField
                        label="Away team"
                        size="small"
                        fullWidth
                        value={form.awayTeam}
                        onChange={(e) => updateLegForm(i, 'awayTeam', e.target.value)}
                      />
                      <TextField
                        label="Home team"
                        size="small"
                        fullWidth
                        value={form.homeTeam}
                        onChange={(e) => updateLegForm(i, 'homeTeam', e.target.value)}
                      />
                      <TextField
                        label="Game start"
                        type="datetime-local"
                        size="small"
                        sx={{ minWidth: 210 }}
                        InputLabelProps={{ shrink: true }}
                        value={form.date}
                        onChange={(e) => updateLegForm(i, 'date', e.target.value)}
                      />
                    </Stack>
                  </Stack>
                </Box>
              ))}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setLegForms((prev) => [...prev, emptyLegForm()])}
                >
                  Add leg (parlay)
                </Button>
                <Box sx={{ flex: 1 }} />
                <TextField
                  label="Sportsbook (optional)"
                  size="small"
                  value={manualBook}
                  onChange={(e) => setManualBook(e.target.value)}
                />
                <TextField
                  label="Stake ($)"
                  size="small"
                  sx={{ width: 140 }}
                  value={manualStake}
                  onChange={(e) => setManualStake(e.target.value)}
                />
                <Button variant="contained" onClick={handleManualSave} sx={{ fontWeight: 700 }}>
                  Save bet
                </Button>
              </Stack>
              {manualError && <Alert severity="error">{manualError}</Alert>}
            </Stack>
          )}

          {addTab === 1 && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Paste a bet as text (&quot;Chiefs -3.5 -110 $50 at DraftKings&quot;), or paste a
                sportsbook share link and we&apos;ll read the slip for you. Either way you confirm
                the parsed draft before anything is saved.
              </Typography>
              <TextField
                multiline
                minRows={3}
                fullWidth
                placeholder="Paste bet text or a share link…"
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
              />
              {pasteError && <Alert severity="error">{pasteError}</Alert>}
              <Box>
                <Button
                  variant="contained"
                  onClick={handleParsePaste}
                  disabled={pasteBusy || !pasteText.trim()}
                  sx={{ fontWeight: 700 }}
                >
                  {pasteBusy ? 'Reading…' : 'Parse bet'}
                </Button>
              </Box>
            </Stack>
          )}

          {addTab === 2 && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Upload a screenshot of your betslip and we&apos;ll read it into the same confirm
                card. Your screenshot is read once to fill the draft and is not stored.
              </Typography>
              {shotNote && <Alert severity="info">{shotNote}</Alert>}
              {shotError && <Alert severity="error">{shotError}</Alert>}
              <Box>
                <Button
                  variant="contained"
                  component="label"
                  startIcon={shotBusy ? <CircularProgress size={16} /> : <PhotoIcon />}
                  disabled={shotBusy}
                  sx={{ fontWeight: 700 }}
                >
                  {shotBusy ? 'Reading…' : 'Upload a screenshot'}
                  <input
                    type="file"
                    hidden
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => {
                      handleScreenshotFile(e.target.files && e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
            </Stack>
          )}

          {addTab === 3 && (
            <Stack spacing={1.5}>
              <Typography variant="body2" color="text.secondary">
                Import a CSV export in the tracker format (columns: book, sport, event, selection,
                betType, oddsAmerican, stake, placedAt, status). Settled rows import with their
                results; cashed-out rows are skipped — the tracker has no cash-out state.
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  component="label"
                  disabled={csvBusy}
                  sx={{ fontWeight: 700 }}
                >
                  {csvBusy ? 'Importing…' : 'Choose CSV file'}
                  <input
                    type="file"
                    hidden
                    accept=".csv,text/csv"
                    onChange={(e) => {
                      handleCsvFile(e.target.files && e.target.files[0]);
                      e.target.value = '';
                    }}
                  />
                </Button>
              </Box>
              {csvReport && (
                <Alert severity={csvReport.imported > 0 ? 'success' : 'warning'}>
                  Imported {csvReport.imported} bet{csvReport.imported === 1 ? '' : 's'}
                  {csvReport.skippedCashedOut > 0 &&
                    ` · skipped ${csvReport.skippedCashedOut} cashed-out row${csvReport.skippedCashedOut === 1 ? '' : 's'}`}
                  {csvReport.errors.length > 0 &&
                    ` · ${csvReport.errors.length} row error${csvReport.errors.length === 1 ? '' : 's'}: ` +
                      csvReport.errors.slice(0, 3).map((e) => `line ${e.line}: ${e.message}`).join('; ')}
                </Alert>
              )}
            </Stack>
          )}

          {renderConfirmCard()}
        </CardContent>
      </Card>

      {/* Exposure Guard (Winning Features addendum) */}
      {exposureRepeats.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Exposure check:</strong>{' '}
          {exposureRepeats
            .map((entry) => `${entry.team} are on ${entry.count} of your tickets`)
            .join(' · ')}
          {' — one bad game can sink every ticket it touches.'}
        </Alert>
      )}

      {/* Bet list */}
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        Your bets
      </Typography>
      {betsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : sortedBets.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No bets yet — add your first one above, or build a parlay and save it here.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {sortedBets.map((bet) => {
            const chip = STATUS_CHIP[bet.status] || STATUS_CHIP.pending;
            const isParlay = bet.type === 'parlay';
            const expanded = Boolean(expandedIds[bet.id]);
            const potentialPayout =
              typeof bet.combinedDecimal === 'number' ? bet.stake * bet.combinedDecimal : null;
            return (
              <Card key={bet.id}>
                <CardContent sx={{ py: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography variant="body1" sx={{ fontWeight: 700 }}>
                        {isParlay
                          ? `${bet.legs.length}-leg parlay`
                          : selectionLabel(bet.legs[0] || {})}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {isParlay
                          ? bet.legs.map((leg) => selectionLabel(leg)).join(' + ')
                          : `${legEventLabel(bet.legs[0] || {})} · ${sportLabelFor((bet.legs[0] || {}).sport)}`}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      {bet.book && <Chip label={bet.book} size="small" variant="outlined" />}
                      <Chip label={SOURCE_LABEL[bet.source] || bet.source} size="small" variant="outlined" />
                      <Chip label={chip.label} size="small" color={chip.color} />
                      {bet.status !== 'pending' && bet.settledBy && (
                        <Chip
                          label={bet.settledBy === 'auto' ? 'Auto-graded' : 'Marked by you'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                    <Box sx={{ textAlign: 'right', minWidth: 150 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatAmerican(bet.combinedAmerican)} · {formatMoney(bet.stake)} stake
                      </Typography>
                      <Typography
                        variant="caption"
                        color={bet.status === 'pending' ? 'text.secondary' : bet.profit > 0 ? 'success.main' : bet.profit < 0 ? 'error.main' : 'text.secondary'}
                      >
                        {bet.status === 'pending'
                          ? potentialPayout !== null
                            ? `Pays ${formatMoney(potentialPayout)}`
                            : ''
                          : `Profit ${formatSignedMoney(bet.profit)}`}
                      </Typography>
                    </Box>
                    {isParlay && (
                      <IconButton
                        size="small"
                        onClick={() => toggleExpanded(bet.id)}
                        aria-label={expanded ? 'Hide legs' : 'Show legs'}
                      >
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(bet)}
                      aria-label="Delete bet"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Collapse in={!isParlay || expanded} unmountOnExit={isParlay}>
                    <Stack spacing={0.5} sx={{ mt: 1 }}>
                      {bet.legs.map((leg, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {selectionLabel(leg)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {legEventLabel(leg)} · {sportLabelFor(leg.sport)} · {formatStartEt(leg.commenceTime)}
                          </Typography>
                          {leg.result && (
                            <Chip
                              label={leg.result}
                              size="small"
                              color={leg.result === 'won' ? 'success' : leg.result === 'lost' ? 'error' : 'warning'}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                            {formatAmerican(leg.american)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Collapse>

                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Mark as:
                    </Typography>
                    {['won', 'lost', 'push'].map((outcome) => (
                      <Button
                        key={outcome}
                        size="small"
                        variant={bet.status === outcome ? 'contained' : 'outlined'}
                        onClick={() => handleOverride(bet, outcome)}
                        disabled={bet.status === outcome && bet.settledBy === 'manual'}
                      >
                        {outcome}
                      </Button>
                    ))}
                    {bet.status === 'pending' && (
                      <Typography variant="caption" color="text.secondary">
                        (or wait — final scores grade it automatically)
                      </Typography>
                    )}
                  </Box>

                  {/* Hedge Calculator (Winning Features addendum, Premium) */}
                  {isPremium && bet.status === 'pending' && typeof bet.combinedDecimal === 'number' && (
                    <>
                      <Box sx={{ mt: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openHedgePanel(bet)}
                          sx={{ fontWeight: 700 }}
                        >
                          {hedgePanel && hedgePanel.betId === bet.id
                            ? 'Hide hedge calculator'
                            : 'Hedge calculator'}
                        </Button>
                      </Box>
                      <Collapse in={Boolean(hedgePanel && hedgePanel.betId === bet.id)} unmountOnExit>
                        {renderHedgePanel(bet)}
                      </Collapse>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 3, textAlign: 'center' }}
      >
        {DISCLAIMER}
      </Typography>
    </Box>
  );
};

export default BetTrackerScreen;
